export type ConnectorTabType =
  | 'integrations'
  | 'workflow'
  | 'virtual-users'
  | 'datasets'
  | 'chat-viewer'
  | 'rag-documents'
  | 'auto-fine-tuning'
  | 'pipelines'
  | 'fine-tuning-graph';

export type ConnectorTabGroup = 'core' | 'data' | 'automation' | 'insights';

export interface ConnectorTabDefinition {
  id: ConnectorTabType;
  label: string;
  shortLabel?: string;
  icon: string;
  eyebrow: string;
  subtitle: string;
  accent: string;
  group: ConnectorTabGroup;
  title: string;
  description: string;
}

export const CONNECTOR_TAB_GROUPS: Record<
  ConnectorTabGroup,
  { label: string; description: string }
> = {
  core: { label: 'Core', description: 'Connect and configure' },
  data: { label: 'Data', description: 'Datasets, chats & knowledge' },
  automation: { label: 'Automation', description: 'Scheduled training flows' },
  insights: { label: 'Insights', description: 'Model lineage & history' },
};

export const CONNECTOR_TABS: ConnectorTabDefinition[] = [
  {
    id: 'integrations',
    label: 'Integrations',
    icon: 'fa-plug',
    eyebrow: 'Core',
    subtitle: 'Connect external systems, pick an integration, and tune agent configs.',
    accent: '#0099FA',
    group: 'core',
    title: 'Integrations',
    description: 'Connect integrations and select one to use across Connector.',
  },
  {
    id: 'workflow',
    label: 'Workflow',
    icon: 'fa-sitemap',
    eyebrow: 'Core',
    subtitle: 'Read, edit, and version the dialogue workflow graph of the connected system.',
    accent: '#0072C6',
    group: 'core',
    title: 'Workflow',
    description: 'Edit the target-system workflow graph with dry-run, apply, and rollback.',
  },
  {
    id: 'virtual-users',
    label: 'Virtual Users',
    shortLabel: 'VUsers',
    icon: 'fa-users',
    eyebrow: 'Data',
    subtitle: 'Create personas, virtual users, and run synthetic dialog experiments.',
    accent: '#0099FA',
    group: 'data',
    title: 'Virtual Users',
    description: 'Create virtual users and personas for experiments.',
  },
  {
    id: 'datasets',
    label: 'Datasets',
    icon: 'fa-database',
    eyebrow: 'Data',
    subtitle: 'Import integration data, export dialogs, and manage training datasets.',
    accent: '#0072C6',
    group: 'data',
    title: 'Datasets',
    description: 'Import datasets and run virtual-user experiments.',
  },
  {
    id: 'chat-viewer',
    label: 'Chat Viewer',
    shortLabel: 'Chats',
    icon: 'fa-comments',
    eyebrow: 'Data',
    subtitle: 'Browse dialog logs, inspect pipeline steps, and label messages.',
    accent: '#0072C6',
    group: 'data',
    title: 'Chat Viewer',
    description: 'Browse chat logs and label messages for review.',
  },
  {
    id: 'rag-documents',
    label: 'RAG Documents',
    shortLabel: 'RAG',
    icon: 'fa-book',
    eyebrow: 'Data',
    subtitle: 'Upload knowledge files, organize folders, and track indexing status.',
    accent: '#0099FA',
    group: 'data',
    title: 'RAG Documents',
    description: 'Upload files and organize folders for RAG.',
  },
  {
    id: 'auto-fine-tuning',
    label: 'Auto Fine-Tuning',
    shortLabel: 'Auto FT',
    icon: 'fa-magic',
    eyebrow: 'Automation',
    subtitle: 'Schedule recurring fine-tuning jobs from collected reactions.',
    accent: '#FF8300',
    group: 'automation',
    title: 'Auto Fine-Tuning',
    description: 'Schedule recurring fine-tuning from collected reactions.',
  },
  {
    id: 'pipelines',
    label: 'Pipelines',
    icon: 'fa-stream',
    eyebrow: 'Automation',
    subtitle: 'Compose full automation cycles: datasets → tuning → fine-tuning.',
    accent: '#0099FA',
    group: 'automation',
    title: 'Pipelines',
    description: 'Schedule full automation pipelines.',
  },
  {
    id: 'fine-tuning-graph',
    label: 'Fine Tuning Graph',
    shortLabel: 'FT Graph',
    icon: 'fa-project-diagram',
    eyebrow: 'Insights',
    subtitle: 'Explore lineage between base models and fine-tuned aliases.',
    accent: '#0072C6',
    group: 'insights',
    title: 'Fine Tuning Graph',
    description: 'View lineage between base and fine-tuned models.',
  },
];

export const CONNECTOR_TAB_MAP = Object.fromEntries(
  CONNECTOR_TABS.map((tab) => [tab.id, tab])
) as Record<ConnectorTabType, ConnectorTabDefinition>;

export function getConnectorTab(id: ConnectorTabType): ConnectorTabDefinition {
  return CONNECTOR_TAB_MAP[id];
}
